// Configuración y helpers para el sistema de "última oportunidad"

/**
 * DURACIÓN DEL PERIODO DE ÚLTIMA OPORTUNIDAD POR DEFECTO
 * Usado cuando la propiedad no tiene una duración específica configurada
 *
 * Rango válido: 24-168 horas (1-7 días)
 */
export const LAST_CHANCE_DURATION = 48; // horas

/**
 * Calcula la fecha de fin del periodo de última oportunidad
 * ✅ MODIFICADO: Ahora prioriza last_chance_deadline si existe
 *
 * @param {object} property - Objeto de propiedad con last_chance_deadline o last_chance_started_at y last_chance_duration
 * @returns {Date|null} - Fecha de fin o null si no hay periodo activo
 */
export const getLastChanceEndDate = (property) => {
  // Prioridad 1: usar deadline si existe (nuevo formato)
  if (property?.last_chance_deadline) {
    return new Date(property.last_chance_deadline);
  }

  // Fallback: calcular con duration (legacy)
  if (!property?.last_chance_started_at) {
    return null;
  }

  const startDate = new Date(property.last_chance_started_at);
  const duration = property.last_chance_duration || LAST_CHANCE_DURATION;
  const endDate = new Date(startDate.getTime() + duration * 60 * 60 * 1000);

  return endDate;
};

/**
 * Verifica si una propiedad está actualmente en periodo de última oportunidad
 * @param {object} property - Objeto de propiedad
 * @returns {boolean} - true si está en periodo de última oportunidad
 */
export const isInLastChance = (property) => {
  if (!property?.last_chance_started_at) {
    return false;
  }

  const now = new Date();
  const endDate = getLastChanceEndDate(property);

  return endDate && now < endDate;
};

/**
 * Verifica si un usuario puede pujar en el periodo de última oportunidad
 * ✅ MODIFICADO: Ahora permite a TODOS los usuarios invitados participar
 *
 * Requisitos:
 * - No debe haber pujado ya durante el periodo de última oportunidad
 *
 * @param {string} userEmail - Email del usuario
 * @param {array} propertyBids - Todas las pujas de la propiedad
 * @param {string} propertyId - ID de la propiedad
 * @returns {boolean} - true si el usuario puede pujar
 */
export const canUserBidInLastChance = (userEmail, propertyBids, propertyId) => {
  // FILTRO DEFENSIVO: Asegurar que solo se evalúan pujas de ESTA propiedad
  const bidsFromThisProperty = propertyBids.filter(
    b => b.propertyId === propertyId
  );

  // Filtrar pujas del usuario EN ESTA PROPIEDAD
  const userBids = bidsFromThisProperty.filter(
    b => b.buyerEmail === userEmail
  );

  // Verificar si ya pujó en periodo de última oportunidad
  const lastChanceBids = userBids.filter(b => b.is_last_chance);

  // Solo puede pujar una vez en última oportunidad
  return lastChanceBids.length === 0;
};

/**
 * Valida una puja durante el periodo de última oportunidad
 * ✅ MODIFICADO: Ahora acepta usuarios sin pujas previas (startPrice como mínimo)
 *
 * Reglas especiales:
 * - Si el usuario NO tiene pujas previas: debe ser >= startPrice y <= maxBid
 * - Si el usuario SÍ tiene pujas previas: debe ser >= su última puja y <= maxBid
 * - No puede superar la puja máxima
 *
 * @param {number} amount - Cantidad que el usuario quiere pujar
 * @param {number|null} userLastBid - Última puja del usuario (null si no tiene)
 * @param {number} maxBid - Puja máxima actual de la propiedad
 * @param {number} startPrice - Precio inicial de la propiedad
 * @returns {object} - { valid: boolean, error: string }
 */
export const validateLastChanceBid = (amount, userLastBid, maxBid, minAbsolute) => {
  // Determinar el mínimo: su última puja + 1€ o el precio inicial
  const minBid = userLastBid ? userLastBid + 1 : minAbsolute;

  // Validar que el monto esté dentro del rango permitido
  if (amount < minBid) {
    return {
      valid: false,
      errorKey: userLastBid
        ? 'validation.lastChance.belowPrevBid'
        : 'validation.lastChance.belowMinPrice',
      errorParams: { min: minBid.toLocaleString() },
    };
  }

  if (amount > maxBid) {
    return {
      valid: false,
      errorKey: 'validation.lastChance.aboveMax',
      errorParams: { max: maxBid.toLocaleString() },
    };
  }

  // Validación exitosa
  return {
    valid: true,
    error: ''
  };
};

/**
 * Obtiene el rango válido para una puja de última oportunidad
 * ✅ MODIFICADO: Ahora soporta usuarios sin pujas previas
 *
 * @param {number|null} userLastBid - Última puja del usuario (null si no tiene)
 * @param {number} maxBid - Puja máxima actual
 * @param {number} startPrice - Precio inicial de la propiedad
 * @returns {object} - { min: number, max: number }
 */
export const getLastChanceBidRange = (userLastBid, maxBid, minAbsolute) => {
  // Si el usuario tiene una puja previa, el mínimo es su puja + 1€
  // Si no tiene pujas, el mínimo es el precio mínimo absoluto
  const min = userLastBid ? userLastBid + 1 : minAbsolute;

  return {
    min,
    max: maxBid
  };
};

/**
 * Formatea el mensaje de ayuda para el formulario de última oportunidad
 * ✅ MODIFICADO: Ahora soporta usuarios sin pujas previas
 *
 * @param {number|null} userLastBid - Última puja del usuario (null si no tiene)
 * @param {number} maxBid - Puja máxima actual
 * @param {number} startPrice - Precio inicial de la propiedad
 * @returns {string} - Mensaje formateado
 */
export const getLastChanceHelperText = (userLastBid, maxBid, minAbsolute) => {
  // Si el usuario tiene puja previa, el mínimo es su puja + 1€
  const minBid = userLastBid ? userLastBid + 1 : minAbsolute;
  const referenceText = userLastBid
    ? 'tu última oferta + 1€'
    : 'la oferta mínima';

  return `Solo puedes ajustar tu oferta entre ${minBid.toLocaleString()}€ (${referenceText}) y ${maxBid.toLocaleString()}€. No puedes superar la oferta máxima.`;
};

/**
 * Verifica si debe iniciarse automáticamente el periodo de última oportunidad
 * Se activa cuando:
 * - El deadline normal ha pasado
 * - Aún no se ha iniciado el periodo de última oportunidad
 * - Hay al menos una puja
 *
 * @param {object} property - Objeto de propiedad
 * @param {array} propertyBids - Pujas de la propiedad
 * @returns {boolean} - true si debe iniciarse
 */
export const shouldStartLastChance = (property, propertyBids) => {
  if (!property) return false;

  const now = new Date();
  const deadline = new Date(property.deadline);

  // El deadline debe haber pasado
  if (now < deadline) {
    return false;
  }

  // No debe haberse iniciado ya
  if (property.last_chance_started_at) {
    return false;
  }

  // Debe haber al menos una puja
  if (!propertyBids || propertyBids.length === 0) {
    return false;
  }

  return true;
};
